/*
 * This example shows how you can use custom nodes and edges to dynamically add elements to your react flow graph.
 * A global layouting function calculates the new positions for the nodes every time the graph changes and animates existing nodes to their new position.
 *
 * There are three ways of adding nodes to the graph:
 *  1. Click an existing node: Create a new child node of the clicked node
 *  2. Click on the plus icon of an existing edge: Create a node in between the connected nodes of the edge
 *  3. Click a placeholder node: Turn the placeholder into a "real" node to prevent jumping of the layout
 *
 * The graph elements are added via hook calls in the custom nodes and edges. The layout is calculated every time the graph changes (see hooks/useLayout.ts).
 */
import ReactFlow, {
  Background,
  Edge,
  Node,
  ProOptions,
  ReactFlowProvider,
} from 'reactflow';

import useLayout from '../../hooks/useLayout';
import nodeTypes from '../../components/reactFlow/NodeTypes';
import edgeTypes from '../../components/reactFlow/EdgeTypes';

import 'reactflow/dist/style.css';

interface Props {
  nodes: Node[];
  edges: Edge[];
  onNodeClick: any;
}

const proOptions: ProOptions = { account: 'paid-pro', hideAttribution: true };

const fitViewOptions = {
  padding: 0.95,
};

function ReactFlowPro({ nodes, edges, onNodeClick }: Props) {
  // this hook call ensures that the layout is re-calculated every time the graph changes
  useLayout();

  return (
    <div style={{ height: '100%' }}>
      <ReactFlow
        defaultNodes={nodes}
        defaultEdges={edges}
        nodes={nodes}
        edges={edges}
        proOptions={proOptions}
        fitView
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitViewOptions={fitViewOptions}
        minZoom={0.2}
        nodesDraggable={true}
        nodesConnectable={false}
        zoomOnDoubleClick={false}
        onNodeClick={onNodeClick}
        // we are setting deleteKeyCode to null to prevent the deletion of nodes in order to keep the example simple.
        // If you want to enable deletion of nodes, you need to make sure that you only have one root node in your graph.
        deleteKeyCode={null}
        className="mt-6"
      >
        <Background />
      </ReactFlow>
    </div>
  );
}

function ReactFlowWrapper({ nodes, edges, onNodeClick }: Props) {
  return (
    <ReactFlowProvider>
      <ReactFlowPro nodes={nodes} edges={edges} onNodeClick={onNodeClick} />
    </ReactFlowProvider>
  );
}

export default ReactFlowWrapper;
